/* eslint-disable max-len */
import {ReloadOutlined} from '@ant-design/icons';
import {Col, Flex, Button, Form, Input, InputNumber, Row} from 'antd';
import React, {useState} from 'react';

import Loader from '../../../components/Loader/Loader';
import PageContent from '../../../components/PageContent/PageContent';
import MainTableTitle from '../../../components/Table/Utils/MainTableTitle';
import {resultsInterpretationInitialValues} from '../constants/pcfDefaultValues';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  MOCK_STAGE_BREAKDOWN,
  MOCK_HOTSPOTS,
  buildMockResultsExtras
} from '../constants/pcfResultsMock';
import usePCFPersist from '../hooks/usePCFPersist';
import {usePCFStepHydration} from '../hooks/usePCFStepHydration';
import {
  ResultsInterpretationKeys,
  ResultsInterpretationLabels
} from '../PCFKeyLabels';

import PCFFooterBackButton from './PCFFooterBackButton';
import PCFFooterPrimaryButton from './PCFFooterPrimaryButton';
import {runPCFDraftSave} from './pcfFormDraft';
import PCFFormHeader from './PCFFormHeader';
import StatusBanner from './StatusBanner';
import TabStepper from './TabStepper';

// Small info icon used inline
const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="M8 11V7"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <circle cx="8" cy="5" r="0.7" fill="currentColor" />
  </svg>
);

// ---- Data for the page ----
const stageBreakdown: StageBarRow[] = MOCK_STAGE_BREAKDOWN;
const hotspotRows = MOCK_HOTSPOTS;

const donutLegend = [
  {label: 'Road - Truck (Diesel) transport', color: '#6366F1'},
  {label: 'Grid Electricity', color: '#8B5CF6'},
  {label: 'Polypropylene resin input', color: '#22D3EE'},
  {label: 'Packaging material', color: '#10B981'},
  {label: 'Waste handling', color: '#F59E0B'}
];

const excludedRows = [
  {row: 'Lubricant top-up', impact: '0.08 kg CO₂e'},
  {row: 'Ancillary cleaning chemical', impact: '0.14 kg CO₂e'}
];

const resultsValidateTrigger: Array<'onSubmit'> = ['onSubmit'];

interface StageBarRow {
  label: string;
  value: number;
  color: string;
}
interface DonutSegment {
  value: number;
  color: string;
}

// ===== Horizontal bar chart =====
function BarChart({data, max = 16}: {data: StageBarRow[]; max: number}) {
  const safeMax = max > 0 ? max : 1;
  const W = 720;
  const H = 280;
  const pad = {top: 12, right: 24, bottom: 32, left: 32};
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;
  const rowH = innerH / data.length;
  const barH = rowH * 0.55;

  // X axis ticks every 2 from 0 to 16
  const tickStep = 2;
  const tickCount = Math.floor(safeMax / tickStep) + 1;
  const ticks = Array.from({length: tickCount}, (_, i) => i * tickStep);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bar-chart">
      {/* Grid lines */}
      {ticks.map((t) => {
        const x = pad.left + (t / safeMax) * innerW;

        return (
          <line
            key={t}
            x1={x}
            x2={x}
            y1={pad.top}
            y2={H - pad.bottom}
            stroke="#E5E7EB"
            strokeWidth="1"
          />
        );
      })}

      {/* Bars + row labels */}
      {data?.map((d: StageBarRow, i: number) => {
        const y = pad.top + i * rowH + (rowH - barH) / 2;
        const w = (d.value / safeMax) * innerW;

        return (
          <g key={i}>
            <text
              x={pad.left - 10}
              y={y + barH / 2 + 4}
              textAnchor="end"
              fontSize="11"
              fontWeight="500"
              fill="#5B5D6B"
            >
              {d.label}
            </text>
            <rect
              x={pad.left}
              y={y}
              width={w}
              height={barH}
              rx="3"
              fill={d.color}
            />
          </g>
        );
      })}

      {/* X axis tick labels */}
      {ticks.map((t) => {
        const x = pad.left + (t / safeMax) * innerW;

        return (
          <text
            key={t}
            x={x}
            y={H - pad.bottom + 18}
            textAnchor="middle"
            fontSize="11"
            fontWeight="500"
            fill="#777986"
          >
            {t}
          </text>
        );
      })}
    </svg>
  );
}

// ===== Donut chart =====
function DonutChart({segments}: {segments: DonutSegment[]}) {
  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const r = 70;
  const inner = 50;

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  let cumulative = 0;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      {segments.map((seg, i) => {
        const startAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;
        cumulative += seg.value;
        const endAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;

        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        const x3 = cx + inner * Math.cos(endAngle);
        const y3 = cy + inner * Math.sin(endAngle);
        const x4 = cx + inner * Math.cos(startAngle);
        const y4 = cy + inner * Math.sin(startAngle);

        const large = endAngle - startAngle > Math.PI ? 1 : 0;

        const d = [
          `M ${x1} ${y1}`,
          `A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`,
          `L ${x3} ${y3}`,
          `A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4}`,
          'Z'
        ].join(' ');

        return <path key={i} d={d} fill={seg.color} />;
      })}
    </svg>
  );
}

// ===== Page =====
const ResultsInterpretationPage: React.FC = () => {
  const [form] = Form.useForm();
  const [submitFailed, setSubmitFailed] = useState(false);

  const {recordId, isStepReady, navigateToStep, reloadFromServer} =
    usePCFStepHydration(form, 'results');
  const {isSaving, saveDraftLabel, actionLabel, persist, runWhileSaving} =
    usePCFPersist();

  const persistResults = async (
    status: 'draft' | 'approved'
  ): Promise<void> => {
    await persist({
      section: 'results',
      sectionValues: form.getFieldsValue(true),
      status,
      resultsExtras: buildMockResultsExtras()
    });
  };

  const handleSaveDraft = (): void => {
    runWhileSaving(async () => {
      setSubmitFailed(false);
      await runPCFDraftSave(form, async () => {
        await persistResults('draft');
      });
    }).catch(() => {});
  };

  const handleProceed = (): void => {
    runWhileSaving(async () => {
      await form.validateFields();
      setSubmitFailed(false);
      await persistResults('approved');
      navigateToStep('reportExport');
    }).catch(() => {
      setSubmitFailed(true);
      setTimeout(() => {
        document
          .querySelector('.ant-form-item-has-error')
          ?.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 50);
    });
  };

  const showFormErrorSummary =
    submitFailed && form.getFieldsError().some(({errors}) => errors.length > 0);

  // Donut data using hotspot percentages (top 5 hotspots, equal split for visual)
  const donutSegments: DonutSegment[] = [
    {value: 50, color: '#6366F1'},
    {value: 20, color: '#8B5CF6'},
    {value: 18, color: '#22D3EE'},
    {value: 8, color: '#10B981'},
    {value: 4, color: '#F59E0B'}
  ];
  const stageBreakdownMax = Math.max(
    1,
    ...stageBreakdown.map((stage) => stage.value)
  );

  return (
    <Flex
      vertical
      justify="space-between"
      align="center"
      gap={24}
      style={{height: '100%'}}
    >
      <Flex
        vertical={false}
        justify="space-between"
        align="center"
        style={{width: '100%'}}
      >
        <MainTableTitle
          title="Product Carbon Footprint (PCF)"
          showDivider={false}
        />
        <Flex vertical={false} gap={10} align="center" justify="center">
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => {
              reloadFromServer().catch(() => {});
            }}
            disabled={isSaving}
          />
          <Button
            type="primary"
            htmlType="button"
            onClick={handleSaveDraft}
            loading={isSaving}
            disabled={isSaving}
          >
            {saveDraftLabel}
          </Button>
        </Flex>
      </Flex>
      <TabStepper
        activeStep={4}
        completedSteps={[1, 2, 3]}
        recordId={recordId}
      />
      <StatusBanner
        title="Status"
        message="PCF calculated successfully. Results have been refreshed."
      />
      <PageContent>
        <div className="results-interpretation-page">
          {!isStepReady ? (
            <Loader topMargin />
          ) : (
            <>
              <PCFFormHeader
                title="4 . Results, Hotspot Analysis & Interpretation"
                description="Read-only output panels plus mandatory interpretation fields."
              />

              <Flex
                vertical
                gap={24}
                align="center"
                justify="center"
                style={{width: '100%'}}
              >
                {/* Total PCF Result subcard */}
                <div className="subcard">
                  <div className="subcard-header">
                    <div className="subcard-title-row">
                      <h3 className="subcard-title">Total PCF Result</h3>
                      <InfoIcon />
                    </div>
                    <p className="subcard-subtitle">
                      Calculated output. Read-only display.
                    </p>
                  </div>

                  <div className="total-pcf-grid">
                    {/* Left: Primary Output big tile */}
                    <div className="primary-output-tile">
                      <div className="primary-output-label">Primary Output</div>
                      <div className="primary-output-value">
                        <span className="primary-output-number">20.52</span>
                        <span className="primary-output-unit">
                          kg CO<sub>2</sub>e per 1 kg
                        </span>
                      </div>
                      <p className="primary-output-caption">
                        Calculated as the sum of all included life cycle stage
                        GWP contributions (A1 + A2 + A3 + ... + C4). Module D is
                        displayed separately and is not included in the total.
                      </p>
                    </div>

                    {/* Right column: 3 stacked metrics */}
                    <div className="metric-stack">
                      <div className="metric-tile">
                        <div className="metric-label-row">
                          <span>Beyond System Boundary (Module D)</span>
                          <InfoIcon />
                        </div>
                        <div className="metric-value">
                          0.00{' '}
                          <span className="metric-unit">
                            kg CO<sub>2</sub>e
                          </span>
                        </div>
                      </div>

                      <div className="metric-tile">
                        <div className="metric-label-row">
                          <span>Uncertainty Range (95% CI)</span>
                          <InfoIcon />
                        </div>
                        <div className="metric-value">
                          18.05 ± 1.28{' '}
                          <span className="metric-unit">
                            kg CO<sub>2</sub>e
                          </span>
                        </div>
                      </div>

                      <div className="metric-tile">
                        <div className="metric-label-row">
                          <span>Data Quality Score</span>
                          <InfoIcon />
                        </div>
                        <div className="metric-value-row">
                          <span className="metric-value-text">High</span>
                          <span className="quality-pill">78% primary data</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bar chart subcard */}
                <div className="subcard">
                  <div className="subcard-title-row">
                    <h3 className="subcard-title">
                      PCF Breakdown by Life Cycle Stage
                    </h3>
                    <InfoIcon />
                  </div>
                  <div className="bar-chart-wrapper">
                    <BarChart data={stageBreakdown} max={stageBreakdownMax} />
                  </div>
                </div>

                {/* Top 5 hotspots subcard */}
                <div className="subcard">
                  <div className="subcard-header">
                    <div className="subcard-title-row">
                      <h3 className="subcard-title">Top 5 Emission Hotspots</h3>
                      <InfoIcon />
                    </div>
                    <p className="subcard-subtitle">
                      Top contributors driving total PCF.
                    </p>
                  </div>

                  <div className="hotspots-grid">
                    {/* Donut */}
                    <div className="donut-block">
                      <div className="donut-wrap">
                        <DonutChart segments={donutSegments} />
                      </div>
                      <Flex
                        vertical={false}
                        gap={8}
                        justify="center"
                        wrap="wrap"
                      >
                        {donutLegend.map((l, i) => (
                          <div key={i} className="donut-legend-item">
                            <span
                              className="donut-legend-dot"
                              style={{background: l.color}}
                            />
                            <span>{l.label}</span>
                          </div>
                        ))}
                      </Flex>
                    </div>

                    {/* Hotspot table */}
                    <div className="hotspot-table-wrap">
                      <table className="hotspot-table">
                        <thead>
                          <tr>
                            <th style={{width: '60px'}}>Rank</th>
                            <th>Process / Material</th>
                            <th style={{width: '80px'}}>Stage</th>
                            <th style={{width: '90px'}}>
                              kg CO<sub>2</sub>e
                            </th>
                            <th style={{width: '90px'}}>% of Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hotspotRows.map((r) => (
                            <tr key={r.rank}>
                              <td>
                                <span className="rank-pill">#{r.rank}</span>
                              </td>
                              <td>{r.process}</td>
                              <td>{r.stage}</td>
                              <td>{r.value.toFixed(2)}</td>
                              <td>{r.percent.toFixed(1)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Additional Result Panels */}
                <div className="subcard">
                  <div className="subcard-header">
                    <h3 className="subcard-title">Additional Result Panels</h3>
                    <p className="subcard-subtitle">
                      Conditional outputs based on upstream settings and
                      exclusions.
                    </p>
                  </div>

                  <div className="addl-panels-grid">
                    {/* Fossil vs Biogenic */}
                    <div className="panel-tile">
                      <div className="panel-tile-header">
                        <span>Fossil vs Biogenic GWP Split</span>
                        <InfoIcon />
                      </div>
                      <Flex
                        vertical
                        gap={4}
                        align="start"
                        className="panel-row"
                      >
                        <div className="panel-row-label">GWP-fossil</div>
                        <div className="panel-row-value">
                          17.42{' '}
                          <span className="panel-row-unit">
                            kg CO<sub>2</sub>e
                          </span>
                        </div>
                      </Flex>
                      <Flex
                        vertical
                        gap={4}
                        align="start"
                        className="panel-row"
                      >
                        <div className="panel-row-label">GWP-biogenic</div>
                        <div className="panel-row-value">
                          0.63{' '}
                          <span className="panel-row-unit">
                            kg CO<sub>2</sub>e
                          </span>
                        </div>
                      </Flex>
                    </div>

                    {/* Excluded rows summary */}
                    <div className="panel-tile">
                      <div className="panel-tile-header">
                        <span>Excluded rows summary</span>
                        <InfoIcon />
                      </div>
                      <table className="excluded-table">
                        <thead>
                          <tr>
                            <th>Excluded Row</th>
                            <th style={{textAlign: 'right'}}>
                              Estimated Impact
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {excludedRows.map((r, i) => (
                            <tr key={i}>
                              <td>{r.row}</td>
                              <td style={{textAlign: 'right'}}>{r.impact}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Interpretation & Benchmark */}
                <div className="subcard">
                  <div className="subcard-header">
                    <h3 className="subcard-title">
                      Interpretation &amp; Benchmark
                    </h3>
                    <p className="subcard-subtitle">
                      User input section for report narrative, limitations, and
                      optional benchmark comparison.
                    </p>
                  </div>

                  <div className="interpretation-fields">
                    <Form
                      form={form}
                      layout="vertical"
                      initialValues={resultsInterpretationInitialValues}
                      validateTrigger={resultsValidateTrigger}
                      onValuesChange={() => {
                        if (submitFailed) setSubmitFailed(false);
                      }}
                    >
                      <Row gutter={rowGutter}>
                        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
                          <Form.Item
                            name={ResultsInterpretationKeys.KEY_FINDINGS}
                            required
                            label={ResultsInterpretationLabels.KEY_FINDINGS}
                            labelCol={{span: 24}}
                            rules={[
                              {
                                validator: async (_, value) => {
                                  const len =
                                    value != null
                                      ? String(value).trim().length
                                      : 0;
                                  if (len < 50) {
                                    return await Promise.reject(
                                      new Error(
                                        `Minimum 50 characters required (currently ${len}).`
                                      )
                                    );
                                  }

                                  await Promise.resolve();
                                }
                              }
                            ]}
                            validateFirst
                            hasFeedback
                          >
                            <Input.TextArea
                              placeholder="Summarise your key findings. What are the main emission drivers? What reduction opportunities exist? (minimum 50 characters)"
                              rows={4}
                              maxLength={1000}
                              showCount
                            />
                          </Form.Item>
                        </Col>
                        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
                          <Form.Item
                            name={ResultsInterpretationKeys.LIMITATIONS}
                            label={ResultsInterpretationLabels.LIMITATIONS}
                            labelCol={{span: 24}}
                          >
                            <Input.TextArea
                              rows={3}
                              maxLength={500}
                              showCount
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <div className="benchmark-box">
                        <div className="benchmark-box-header">
                          <span>Comparison Benchmark (Optional)</span>
                          <InfoIcon />
                        </div>
                        <Row gutter={rowGutter}>
                          <Col
                            className={PCF_COL_GUTTER_CLASS}
                            span={PCF_COL_SPAN_HALF}
                          >
                            <Form.Item
                              name={ResultsInterpretationKeys.BENCHMARK_VALUE}
                              label={
                                ResultsInterpretationLabels.BENCHMARK_VALUE
                              }
                            >
                              <InputNumber
                                controls={false}
                                className="input"
                                placeholder="e.g. 12.5"
                                style={{width: '100%'}}
                                min={0}
                              />
                            </Form.Item>
                          </Col>
                          <Col
                            className={PCF_COL_GUTTER_CLASS}
                            span={PCF_COL_SPAN_HALF}
                          >
                            <Form.Item
                              name={ResultsInterpretationKeys.BENCHMARK_LABEL}
                              label={
                                ResultsInterpretationLabels.BENCHMARK_LABEL
                              }
                            >
                              <Input
                                className="input"
                                placeholder="e.g. Industry average (GreenDelta 2023)"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </Form>
                  </div>
                </div>
              </Flex>

              {showFormErrorSummary && (
                <div className="form-error-summary">
                  Please complete the Key Findings section (minimum 50
                  characters) before proceeding.
                </div>
              )}

              {/* Footer actions */}
              <div className="footer-actions">
                <PCFFooterBackButton
                  disabled={isSaving}
                  onClick={() => {
                    navigateToStep('impactConfiguration');
                  }}
                />
                <PCFFooterPrimaryButton
                  onClick={handleProceed}
                  label={actionLabel('Proceed to Report')}
                  isSaving={isSaving}
                />
              </div>
            </>
          )}
        </div>
      </PageContent>
    </Flex>
  );
};

export default ResultsInterpretationPage;
